/**
 * Technical Sheet PDF Generator (Shopify)
 * Opens a nice preview window with download and print options
 *
 * Dependencies:
 * - html2canvas: https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js
 * - jsPDF: https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js
 */

async function generateTechnicalSheet(data) {
  if (!data || !data.sku) {
    console.error("Technical Sheet Generator: SKU is required");
    return;
  }

  // Helper to load image and convert to base64 (handles both regular images and SVGs)
  async function loadImageAsBase64(url) {
    if (!url) return '';
    
    try {
      const response = await fetch(url, { mode: 'cors' });
      const contentType = response.headers.get('content-type');
      
      // For SVG, we need to convert it to a data URL differently
      if (contentType && contentType.includes('svg')) {
        const svgText = await response.text();
        // Encode SVG as base64 data URL
        const base64 = btoa(unescape(encodeURIComponent(svgText)));
        return 'data:image/svg+xml;base64,' + base64;
      }
      
      // For regular images
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve('');
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn('Could not load image:', url, e);
      return '';
    }
  }

  // Helper to convert SVG to PNG for better PDF compatibility
  async function svgToPng(svgDataUrl, width, height) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = function() {
        const canvas = document.createElement('canvas');
        canvas.width = width * 2; // 2x for better quality
        canvas.height = height * 2;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = function() {
        resolve(svgDataUrl); // Fallback to original
      };
      img.src = svgDataUrl;
    });
  }

  // Pre-load images as base64
  let logoBase64 = await loadImageAsBase64(data.logoUrl);
  const productImgBase64 = await loadImageAsBase64(data.productImageUrl);

  // Convert SVG logo to PNG for better PDF compatibility
  if (logoBase64 && logoBase64.includes('svg')) {
    logoBase64 = await svgToPng(logoBase64, 100, 80);
  }

  // ---------- Build specs rows HTML ----------
  let specsRowsHtml = "";
  if (Array.isArray(data.specs)) {
    const cleanSpecs = data.specs.filter((s) => {
      const hasTitle = s?.title && String(s.title).trim();
      const hasIcons = s?.isIcons && s?.iconsHtml;
      const hasValue = s?.value && String(s.value).trim();
      return hasTitle && (hasIcons || hasValue);
    });

    cleanSpecs.forEach((spec, idx) => {
      const isLast = idx === cleanSpecs.length - 1;
      const borderStyle = isLast ? "none" : "1px solid #d9d9d9";

      const rightContent =
        spec.isIcons && spec.iconsHtml
          ? `<div style="display:inline-flex; gap:8px; align-items:center;">${spec.iconsHtml}</div>`
          : `${spec.value ?? ""}`;

      specsRowsHtml += `
        <tr>
          <td style="
            padding: 10px 0;
            border-bottom: ${borderStyle};
            font-size: 12px;
            color: #000000;
            letter-spacing: 0.2px;
            vertical-align: middle;
          ">${spec.title}</td>
          <td style="
            padding: 10px 0;
            border-bottom: ${borderStyle};
            text-align: right;
            font-size: 12px;
            color: #000000;
            letter-spacing: 0.2px;
            white-space: nowrap;
            vertical-align: middle;
          ">${rightContent}</td>
        </tr>
      `;
    });
  }

  // Product name for display
  const productName = data.productName || '';

  // ---------- Build the full preview page ----------
  const previewHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Technical Sheet - ${productName || data.sku}</title>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"><\/script>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"><\/script>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: Helvetica, Arial, sans-serif;
      background: #ac0033;
      min-height: 100vh;
      padding: 30px 20px;
    }
    
    .page-header {
      max-width: 850px;
      margin: 0 auto 25px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 15px;
    }
    
    .page-title {
      color: white;
      font-size: 24px;
      font-weight: 600;
      text-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }
    
    .page-subtitle {
      color: rgba(255,255,255,0.85);
      font-size: 14px;
      margin-top: 4px;
      font-weight: 400;
    }
    
    .button-group {
      display: flex;
      gap: 12px;
    }
    
    .btn {
      padding: 12px 24px;
      border: none;
      border-radius: 50px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      font-family: Helvetica, Arial, sans-serif;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }
    
    .btn-primary {
      background: white;
      color: #ac0033;
      box-shadow: 0 4px 15px rgba(0,0,0,0.2);
    }
    
    .btn-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0,0,0,0.3);
    }
    
    .btn-secondary {
      background: rgba(255,255,255,0.2);
      color: white;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255,255,255,0.3);
    }
    
    .btn-secondary:hover {
      background: rgba(255,255,255,0.3);
    }
    
    .btn svg {
      width: 18px;
      height: 18px;
    }
    
    .preview-container {
      display: flex;
      justify-content: center;
    }
    
    .preview-wrapper {
      background: white;
      border-radius: 12px;
      box-shadow: 0 25px 50px -12px rgba(0,0,0,0.4);
      overflow: hidden;
    }

    /* Technical Sheet Styles - A4 at 96dpi (794px x 1123px) */
    #technical-sheet-pdf {
      width: 794px;
      height: 1123px;
      padding: 48px 58px;
      background: #ffffff;
      font-family: Helvetica, Arial, sans-serif;
      box-sizing: border-box;
      position: relative;
      color: #000000;
    }

    /* Loading overlay */
    .loading-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.6);
      backdrop-filter: blur(4px);
      display: none;
      justify-content: center;
      align-items: center;
      z-index: 1000;
    }
    
    .loading-overlay.active {
      display: flex;
    }
    
    .loading-card {
      background: white;
      padding: 40px 50px;
      border-radius: 16px;
      text-align: center;
      box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
    }
    
    .spinner {
      width: 48px;
      height: 48px;
      border: 4px solid #f0f0f0;
      border-top: 4px solid #ac0033;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 20px;
    }
    
    .loading-text {
      color: #333;
      font-size: 16px;
      font-weight: 500;
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    /* Print styles */
    @media print {
      body {
        background: white;
        padding: 0;
      }
      .page-header, .loading-overlay {
        display: none !important;
      }
      .preview-wrapper {
        box-shadow: none;
        border-radius: 0;
      }
      .preview-container {
        padding: 0;
      }
    }
  </style>
</head>
<body>
  <div class="page-header">
    <div>
      <h1 class="page-title">${productName || 'Technical Sheet'}</h1>
      <p class="page-subtitle">Reference: ${data.sku}</p>
    </div>
    <div class="button-group">
      <button class="btn btn-primary" onclick="downloadPDF()">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Download PDF
      </button>
      <button class="btn btn-secondary" onclick="window.print()">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
        Print
      </button>
    </div>
  </div>

  <div class="preview-container">
    <div class="preview-wrapper">
      <div id="technical-sheet-pdf">
        <!-- Header -->
        <div style="
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-bottom: 24px;
        ">
          <div style="text-align: left; width: 180px; min-height: 80px;">
            ${logoBase64 ? `<img id="logo-img" src="${logoBase64}" alt="Logo" style="max-width: 160px; max-height: 80px; object-fit: contain;">` : ''}
          </div>
          <div style="font-size: 13px; font-weight: 500; color: #000000; font-family: Helvetica, Arial, sans-serif;">Technical sheet</div>
        </div>

        <!-- Product Name -->
        ${productName ? `
        <div style="margin-top: 6px; margin-bottom: 10px; text-align: center;">
          <div style="
            font-size: 22px;
            font-weight: 700;
            letter-spacing: 0.5px;
            color: #000000;
            text-transform: uppercase;
            font-family: Helvetica, Arial, sans-serif;
          ">${productName}</div>
        </div>
        ` : ''}

        <!-- SKU Box -->
        <div style="
          margin-bottom: 18px;
          height: 44px;
          border: 1px solid #000;
          display: table;
          width: 100%;
        ">
          <div style="
            display: table-cell;
            vertical-align: middle;
            text-align: center;
            font-size: 20px;
            font-weight: 600;
            letter-spacing: 0.6px;
            line-height: 1;
            color: #000000;
            font-family: Helvetica, Arial, sans-serif;
          ">${data.sku}</div>
        </div>

        <!-- Specs Table -->
        <table style="width: 100%; border-collapse: collapse; font-family: Helvetica, Arial, sans-serif;">
          ${specsRowsHtml}
        </table>

        <!-- Product Image -->
        ${productImgBase64 ? `
        <div style="margin-top: 22px;">
          <img src="${productImgBase64}" alt="Product" style="max-width: 240px; max-height: 180px; object-fit: contain; display: block;">
        </div>
        ` : ''}

        <!-- Footer -->
        <div style="
          position: absolute;
          left: 58px;
          right: 58px;
          bottom: 42px;
        ">
          <div style="
            border-top: 1px solid #d9d9d9;
            padding-top: 10px;
            text-align: right;
            font-size: 11px;
            color: #000000;
            letter-spacing: 0.2px;
            font-family: Helvetica, Arial, sans-serif;
          ">
            MANUFACTURER: ${data.manufacturer || 'Khalil Warde S.a.l'}
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="loading-overlay" id="loading">
    <div class="loading-card">
      <div class="spinner"></div>
      <div class="loading-text">Generating PDF...</div>
    </div>
  </div>

  <script>
    const SKU = "${data.sku}";
    
    async function downloadPDF() {
      const loading = document.getElementById('loading');
      loading.classList.add('active');
      
      const element = document.getElementById('technical-sheet-pdf');
      
      try {
        // Wait for any images to fully render
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          imageTimeout: 15000,
          onclone: function(clonedDoc) {
            // Ensure images are visible in cloned document
            const images = clonedDoc.querySelectorAll('img');
            images.forEach(img => {
              img.style.visibility = 'visible';
              img.style.display = 'block';
            });
          }
        });

        const { jsPDF } = window.jspdf;
        const pdf = new jsPDF({ 
          orientation: 'portrait', 
          unit: 'mm', 
          format: 'a4' 
        });

        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save('Technical_Sheet_' + SKU + '.pdf');
      } catch (err) {
        console.error('Error generating PDF:', err);
        alert('Error generating PDF. Check console for details.');
      } finally {
        loading.classList.remove('active');
      }
    }
    
    // Ensure logo is loaded before allowing download
    window.onload = function() {
      const logoImg = document.getElementById('logo-img');
      if (logoImg && !logoImg.complete) {
        logoImg.onload = function() {
          console.log('Logo loaded successfully');
        };
      }
    };
  <\/script>
</body>
</html>
  `;

  // ---------- Open preview in new window ----------
  const previewWindow = window.open('', '_blank', 'width=950,height=850,scrollbars=yes,resizable=yes');
  
  if (previewWindow) {
    previewWindow.document.write(previewHtml);
    previewWindow.document.close();
  } else {
    alert('Please allow popups to view the technical sheet preview.');
  }
}